import { useEffect, useMemo, useState } from 'react';
import {
  AmendmentDetailOptionType,
  ITenancyCompareProps,
} from '~components/amendment-process/amendment-process-helpers';
import {
  AmendmentDetailOption,
  AmendmentDetailStatusOption,
  AmendmentTypeOption,
} from '~graphql/__generated__/globalTypes';

type latestAmendmentDetailType = {
  targetBookingId: string | null;
  status: AmendmentDetailStatusOption | null;
  type: AmendmentDetailOption | null;
};

function useIsAmendmentPending(
  latestAmendmentDetail: latestAmendmentDetailType | null | undefined
) {
  return useMemo(() => {
    return (
      latestAmendmentDetail?.status === AmendmentDetailStatusOption.PENDING &&
      latestAmendmentDetail?.type !== AmendmentDetailOption.TAKEOVER
    );
  }, [latestAmendmentDetail]);
}

// check original booking is extension
function useIsExtensionBooking(
  status: AmendmentDetailStatusOption | null | undefined,
  type: AmendmentDetailOptionType
) {
  const [isExtension, setIsExtension] = useState(false);

  useEffect(() => {
    if (
      status === AmendmentDetailStatusOption.PENDING &&
      (type === AmendmentDetailOption.TENANCY_EXTEND_EARLY_CHECK_IN ||
        type === AmendmentDetailOption.TENANCY_EXTEND_LATER_CHECK_OUT)
    ) {
      setIsExtension(true);
    }
  }, [status, type]);

  return isExtension;
}

function useIsExtensionNewBooking(type: AmendmentDetailOptionType) {
  const [isExtension, setIsExtension] = useState(false);

  useEffect(() => {
    if (
      type === AmendmentDetailOption.TENANCY_EXTEND_EARLY_CHECK_IN ||
      type === AmendmentDetailOption.TENANCY_EXTEND_LATER_CHECK_OUT
    ) {
      setIsExtension(true);
    }
  }, [type]);

  return isExtension;
}

function useAmendmentIsExtension(option: AmendmentDetailOptionType) {
  return useMemo(() => {
    return (
      option === AmendmentDetailOption.TENANCY_EXTEND_EARLY_CHECK_IN ||
      option === AmendmentDetailOption.TENANCY_EXTEND_LATER_CHECK_OUT
    );
  }, [option]);
}

function useAmendmentIsInstallment(option: AmendmentDetailOptionType) {
  return useMemo(() => {
    return option === AmendmentDetailOption.INSTALLMENT;
  }, [option]);
}

function useAmendmentIsTenancy(option: AmendmentDetailOptionType) {
  return useMemo(() => {
    return (
      option === AmendmentDetailOption.TENANCY_CHANGE_ENTIRELY ||
      option === AmendmentDetailOption.TENANCY_EXTEND_EARLY_CHECK_IN ||
      option === AmendmentDetailOption.TENANCY_EXTEND_LATER_CHECK_OUT ||
      option === AmendmentDetailOption.TENANCY_SHORTEN_EARLY_CHECK_OUT ||
      option === AmendmentDetailOption.TENANCY_SHORTEN_LATER_CHECK_IN
    );
  }, [option]);
}

function useAmendmentIsService(option: AmendmentDetailOptionType) {
  return useMemo(() => {
    return option === AmendmentDetailOption.SERVICE;
  }, [option]);
}

function useAmendmentIsLock(
  option: AmendmentDetailOptionType,
  editable: boolean | null | undefined
) {
  return useMemo(() => {
    return (
      option !== AmendmentDetailOption.TENANCY_EXTEND_EARLY_CHECK_IN &&
      option !== AmendmentDetailOption.TENANCY_EXTEND_LATER_CHECK_OUT &&
      !editable
    );
  }, [editable, option]);
}

// compare to check amendment
function useCompareAmendmentIsBed(
  bedId: string | null | undefined,
  sourceBedId: string | null | undefined
) {
  return useMemo(() => {
    return bedId !== sourceBedId;
  }, [bedId, sourceBedId]);
}

function useCompareAmendmentIsTenancy(
  tenancyOption: ITenancyCompareProps | null | undefined,
  sourceTenancyOption: ITenancyCompareProps | null | undefined
) {
  return useMemo(() => {
    if (
      tenancyOption?.tenancyOptionId !== sourceTenancyOption?.tenancyOptionId ||
      tenancyOption?.startDate !== sourceTenancyOption?.startDate ||
      tenancyOption?.endDate !== sourceTenancyOption?.endDate
    ) {
      return true;
    }
    return false;
  }, [sourceTenancyOption, tenancyOption]);
}

// for revamp booking amendment after check in
function useAmendmentAfterCheckinShorten(
  amendmentTypes: (AmendmentTypeOption | null)[] | null | undefined
) {
  return useMemo(() => {
    return amendmentTypes?.some(
      item => item === AmendmentTypeOption.END_DATE || item === AmendmentTypeOption.START_DATE
    );
  }, [amendmentTypes]);
}

function useAmendmentAfterCheckinInstallment(
  amendmentTypes: (AmendmentTypeOption | null)[] | null | undefined
) {
  return useMemo(() => {
    return amendmentTypes?.some(item => item === AmendmentTypeOption.INSTALLMENT);
  }, [amendmentTypes]);
}

function useAmendmentAfterCheckinService(
  amendmentTypes: (AmendmentTypeOption | null)[] | null | undefined
) {
  return useMemo(() => {
    return amendmentTypes?.some(item => item === AmendmentTypeOption.SERVICE);
  }, [amendmentTypes]);
}

function useAmendmentAfterCheckinBed(
  amendmentTypes: (AmendmentTypeOption | null)[] | null | undefined
) {
  return useMemo(() => {
    return amendmentTypes?.some(item => item === AmendmentTypeOption.BED);
  }, [amendmentTypes]);
}

export {
  useAmendmentAfterCheckinBed,
  useAmendmentAfterCheckinInstallment,
  useAmendmentAfterCheckinService,
  useAmendmentAfterCheckinShorten,
  useAmendmentIsExtension,
  useAmendmentIsInstallment,
  useAmendmentIsLock,
  useAmendmentIsService,
  useAmendmentIsTenancy,
  useCompareAmendmentIsBed,
  useCompareAmendmentIsTenancy,
  useIsAmendmentPending,
  useIsExtensionBooking,
  useIsExtensionNewBooking,
};
